/**
 * Currency Ordering Utilities
 * 
 * Provides functions to sort currencies according to workspace currency order settings.
 */

import { Currency } from "../types";

/**
 * Sort currencies according to workspace currency order
 * 
 * @param currencies - Array of currencies to sort
 * @param currencyOrder - Array of currency codes in desired order (from workspace settings)
 * @param defaultCurrency - Default currency code (always shown first)
 * @returns Sorted array of currencies
 */
export function sortCurrenciesByOrder(
  currencies: Currency[],
  currencyOrder: string[],
  defaultCurrency: string
): Currency[] {
  // Keep default currency in the list so validation/formatting get its decimalPlaces (e.g. AMD with 2 decimals)
  const defaultCurrencyObj = currencies.find((c) => c.code === defaultCurrency);
  const otherCurrencies = currencies.filter((c) => c.code !== defaultCurrency);

  if (currencyOrder.length === 0) {
    // No custom order, use alphabetical; include default first if present
    const sorted = otherCurrencies.sort((a, b) => a.code.localeCompare(b.code));
    return defaultCurrencyObj ? [defaultCurrencyObj, ...sorted] : sorted;
  }

  // Create a map for quick lookup
  const orderMap = new Map<string, number>();
  currencyOrder.forEach((code, index) => {
    orderMap.set(code, index);
  });

  // Sort currencies according to order
  const sorted = otherCurrencies.sort((a, b) => {
    const aIndex = orderMap.get(a.code);
    const bIndex = orderMap.get(b.code);

    // If both are in the order, sort by their position
    if (aIndex !== undefined && bIndex !== undefined) {
      return aIndex - bIndex;
    }

    // If only one is in the order, prioritize it
    if (aIndex !== undefined) {
      return -1;
    }
    if (bIndex !== undefined) {
      return 1;
    }

    // If neither is in the order, sort alphabetically
    return a.code.localeCompare(b.code);
  });

  // Add any currencies not in the order at the end (alphabetically sorted)
  const orderedCodes = new Set(currencyOrder);
  const unordered = sorted.filter((c) => !orderedCodes.has(c.code));
  const ordered = sorted.filter((c) => orderedCodes.has(c.code));
  const result = [...ordered, ...unordered.sort((a, b) => a.code.localeCompare(b.code))];

  // Prepend default currency so it is in the list (needed for getCurrencyDecimalPlaces when default is selected)
  return defaultCurrencyObj ? [defaultCurrencyObj, ...result] : result;
}
